/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {MemberDescription} from '../../model/objectDescription';
import {extractAllowedValueLabels} from './advanced-filter-dialog-helpers';

/**
 * Declarative configuration for a single Advanced Filter dropdown row. Wherever a real grid column
 * backs the field, its label and dropdown options are derived from that column (matching the column
 * header shown in the grid) instead of being hardcoded per dialog.
 */
export interface AdvancedFilterFieldConfig {
    /** Unique key used for the row's selection binding, the row-data property to filter on, and the filter summary. */
    key: string;
    /** The resolved column backing this field's label/options, or null for a synthetic (non-data) field. */
    column: MemberDescription | null;
    /** Required when column is null; otherwise defaults to column.displayName (matches the grid's column header). */
    label?: string;
    /** Static dropdown options; used when column is null (e.g. a Yes/No toggle). */
    staticOptions?: string[];
    /** Recomputes this field's dropdown options from the current selections; takes precedence over column/staticOptions. */
    optionsFn?: (selections: Record<string, string>) => string[];
    /** Overrides the label prefix used in the "applied filter" summary chip; defaults to the resolved label. */
    filterLabel?: string;
    /** When false, excluded from the row-filtering predicate (still shown and included in the summary). Defaults to true. */
    includeInFilter?: boolean;
}

/**
 * What the Advanced Filter dialog asks the grid to filter by: the member names and values selected, and the subset
 * of those members whose value has to be looked for within what a row stores rather than compared to it whole.
 */
export interface AdvancedFilterCriteria {
    criteria: Record<string, string[]>;
    partialKeys: string[];
}

/** Builds the ordered Advanced Filter field configs for a componentName from the resolved ObjectDescription members. */
export type AdvancedFilterFieldsBuilder = (members: MemberDescription[]) => AdvancedFilterFieldConfig[];

function findColumn(members: MemberDescription[], memberName: string): MemberDescription | null {
    return members?.find((m: MemberDescription) => m.memberName === memberName) ?? null;
}

export const ADVANCED_FILTER_FIELD_BUILDERS: Record<string, AdvancedFilterFieldsBuilder> = {
    Conduit: (members) => [
        {key: 'areaOfVehicle', column: findColumn(members, 'areaOfVehicle')},
        {key: 'bundleDiameter', column: findColumn(members, 'bundleDiameter')},
        {key: 'baselineThreatType', column: findColumn(members, 'baselineThreatType')}
    ],
    Tape: (members) => {
        const areaOfVehicleColumn = findColumn(members, 'areaOfVehicle');
        const allAreaOfVehicleOptions = extractAllowedValueLabels(areaOfVehicleColumn);
        return [
            {
                key: 'isElectricVehicle',
                column: null,
                label: 'Electric Vehicle?',
                staticOptions: ['Yes', 'No'],
                includeInFilter: false
            },
            {key: 'baselineThreatType', column: findColumn(members, 'baselineThreatType')},
            {
                key: 'areaOfVehicle',
                column: areaOfVehicleColumn,
                optionsFn: (selections: Record<string, string>) => {
                    if (selections['isElectricVehicle'] === 'Yes') {
                        return allAreaOfVehicleOptions.filter(value => !value.includes('ICE'));
                    }
                    if (selections['isElectricVehicle'] === 'No') {
                        return allAreaOfVehicleOptions.filter(value => !value.includes('EV'));
                    }
                    return [];
                }
            },
            {key: 'bundleDiameter', column: findColumn(members, 'bundleDiameter')}
        ];
    },
    HighAbrasionSleeve: (members) => [
        {key: 'areaOfVehicle', column: findColumn(members, 'areaOfVehicle')},
        {key: 'sleeveApplication', column: findColumn(members, 'sleeveApplication')}
    ],
    ThermalReflectiveSleeve: (members) => [
        {key: 'excursionTemperatureCode', column: findColumn(members, 'excursionTemperatureCode')}
    ],
    HeatShrinkTube: (members) => [
        {key: 'wetDryZone', column: findColumn(members, 'wetDryZone')},
        {key: 'areaOfVehicle', column: findColumn(members, 'areaOfVehicle')}
    ]
};
